import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import type { SalesOrder } from '../../types/salesOrder';
import { formatCurrency } from '../../utils/format';

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingHorizontal: 40,
    paddingBottom: 60,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#111',
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  companyBlock: {
    maxWidth: '60%',
  },
  companyName: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 4,
  },
  companyLine: {
    fontSize: 10,
    color: '#555',
  },
  logoImage: {
    maxHeight: 64,
    maxWidth: 160,
    objectFit: 'contain',
  },
  logoCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#1f3c88',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'center',
    paddingHorizontal: 6,
  },
  invoiceTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1f3c88',
    marginBottom: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  summaryColumn: {
    flex: 1,
    paddingRight: 12,
  },
  summaryColumnRight: {
    flex: 1,
    paddingLeft: 12,
    alignItems: 'flex-start',
  },
  summaryLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#666',
    marginBottom: 4,
  },
  summaryBox: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingVertical: 6,
    paddingHorizontal: 6,
    minHeight: 46,
    justifyContent: 'flex-start',
  },
  summaryName: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 2,
  },
  summaryText: {
    fontSize: 9,
    color: '#444',
  },
  metaRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  metaLabel: {
    width: 60,
    fontSize: 9,
    color: '#555',
  },
  metaValue: {
    fontSize: 9,
    fontWeight: 700,
    color: '#111',
  },
  shipRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: 8,
    marginBottom: 20,
  },
  shipLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#666',
    marginRight: 6,
  },
  shipValue: {
    fontSize: 9,
  },
  table: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#d1d5db',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#e5e7f3',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  colDescription: {
    flex: 4,
    paddingLeft: 0,
  },

  colQty: {
    flex: 1,
    textAlign: 'center',
  },

  colRate: {
    flex: 2,
    textAlign: 'right',
  },

  colAmount: {
    flex: 2,
    textAlign: 'right',
    paddingRight: 0,
  },
  tableHeaderText: {
    fontSize: 9,
    fontWeight: 700,
    color: '#4b5563',
  },
  tableCellText: {
    fontSize: 9,
    color: '#111827',
  },
  totalsWrapper: {
    marginTop: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  paymentInfo: {
    flex: 2,
  },
  paymentText: {
    fontSize: 9,
    color: '#444',
    marginBottom: 2,
  },
  totalsBlock: {
    width: 220,
    alignSelf: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  totalLabel: {
    fontSize: 9,
    color: '#6b7280',
  },
  totalValue: {
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'right',
  },
  balanceLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#374151',
  },
  balanceValue: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    textAlign: 'right',
  },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#d1d5db',
  },
});


interface InvoiceProps {
  order: SalesOrder;
  companyInfo?: {
    name: string;
    address: string;
    email?: string;
    phone?: string;
    logoDataUrl?: string | null;
  } | null;
  customer?: {
    name: string;
    billingAddress?: string;
    shippingAddress?: string;
    email?: string;
    phone?: string;
    messageOnInvoice?: string;
  } | null;
  currencySymbol: string;
}

const defaultCompanyInfo = {
  name: 'Coffee Roasters Inc.',
  address: '123 Coffee Bean Lane, Brew City, BC 90210',
  email: 'billing@coffeeroasters.com',
  phone: '+1 (555) 123-4567',
  logoDataUrl: undefined,
};

const formatDisplayDate = (value?: string): string => {
  if (!value) {
    return '';
  }
  const parts = value.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return value;
};

export const SalesOrderInvoicePDF: React.FC<InvoiceProps> = ({
  order,
  companyInfo,
  customer,
  currencySymbol,
}) => {
  const company = companyInfo || defaultCompanyInfo;
  const invoiceDate = order.invoiceDate || order.orderDate || '';
  const dueDate = order.dueDate || '';
  const terms = order.terms || '';
  const shipDate = order.shippingDate || order.expectedShipDate || '';
  const invoiceNote = (order.messageOnInvoice || customer?.messageOnInvoice || '').trim();

  const itemSubtotal = order.items.reduce((sum, item) => {
    const lineAmount = item.amount ?? item.quantity * item.unitPrice;
    return sum + lineAmount;
  }, 0);

  const shippingAmount = order.shippingAmount ?? 0;
  const discountType = order.discountType === 'percent' ? 'percent' : 'amount';
  const discountValue = order.discountValue ?? 0;
  const rawDiscount =
    discountType === 'percent'
      ? (itemSubtotal * Math.min(discountValue, 100)) / 100
      : discountValue;
  const discountAmount = Math.min(Math.max(rawDiscount, 0), itemSubtotal);
  const invoiceTotal = itemSubtotal - discountAmount + shippingAmount;
  // A settled invoice owes nothing. There are no partial payments (spec 100),
  // so this is all-or-nothing. Without this the customer receives a paid
  // invoice demanding the full amount again.
  const balanceDue = order.paymentStatus === 'paid' ? 0 : invoiceTotal;

  const customerName = customer?.name || order.customer;
  const billToLines: string[] = [];
  if (customerName) {
    billToLines.push(customerName);
  }
  if (order.billingAddress) {
    billToLines.push(order.billingAddress);
  } else if (customer?.billingAddress) {
    billToLines.push(customer.billingAddress);
  }

  const shipToLines: string[] = [];
  if (order.shippingAddress) {
    shipToLines.push(order.shippingAddress);
  } else if (order.billingAddress) {
    shipToLines.push(order.billingAddress);
  } else if (customer?.shippingAddress) {
    shipToLines.push(customer.shippingAddress);
  } else if (customer?.billingAddress) {
    shipToLines.push(customer.billingAddress);
  }

  const logoInitials =
    company.name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase() || 'CO';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerTop}>
          <View style={styles.companyBlock}>
            <Text style={styles.companyName}>{company.name}</Text>
            {company.address ? <Text style={styles.companyLine}>{company.address}</Text> : null}
            {company.phone ? <Text style={styles.companyLine}>{company.phone}</Text> : null}
            {company.email ? <Text style={styles.companyLine}>{company.email}</Text> : null}
          </View>
          {company.logoDataUrl ? (
            <Image src={company.logoDataUrl} style={styles.logoImage} />
          ) : (
            <View style={styles.logoCircle}>
              <Text style={styles.logoText}>{logoInitials}</Text>
            </View>
          )}
        </View>

        <Text style={styles.invoiceTitle}>INVOICE</Text>

        <View style={styles.summaryRow}>
          <View style={styles.summaryColumn}>
            <Text style={styles.summaryLabel}>BILL TO</Text>
            <View style={styles.summaryBox}>
              {billToLines.length > 0 ? (
                <>
                  <Text style={styles.summaryName}>{billToLines[0]}</Text>
                  {billToLines.slice(1).map((line) => (
                    <Text key={line} style={styles.summaryText}>
                      {line}
                    </Text>
                  ))}
                </>
              ) : (
                <Text style={styles.summaryText}>No billing address</Text>
              )}
            </View>
          </View>

          <View style={styles.summaryColumn}>
            <Text style={styles.summaryLabel}>SHIP TO</Text>
            <View style={styles.summaryBox}>
              {shipToLines.length > 0 ? (
                shipToLines.map((line) => (
                  <Text key={line} style={styles.summaryText}>
                    {line}
                  </Text>
                ))
              ) : (
                <Text style={styles.summaryText}>No shipping address</Text>
              )}
            </View>
          </View>

          <View style={styles.summaryColumnRight}>
            <Text style={styles.summaryLabel}>INVOICE DETAILS</Text>
            <View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>INVOICE NO.</Text>
                <Text style={styles.metaValue}>{order.orderNumber || 'DRAFT'}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>DATE</Text>
                <Text style={styles.metaValue}>{formatDisplayDate(invoiceDate)}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>DUE DATE</Text>
                <Text style={styles.metaValue}>{formatDisplayDate(dueDate)}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>TERMS</Text>
                <Text style={styles.metaValue}>{terms || '-'}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.shipRow}>
          <Text style={styles.shipLabel}>SHIP DATE</Text>
          <Text style={styles.shipValue}>{formatDisplayDate(shipDate)}</Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.colDescription, styles.tableHeaderText]}>DESCRIPTION</Text>
            <Text style={[styles.colQty, styles.tableHeaderText]}>QTY</Text>
            <Text style={[styles.colRate, styles.tableHeaderText]}>RATE</Text>
            <Text style={[styles.colAmount, styles.tableHeaderText]}>AMOUNT</Text>
          </View>
          {order.items.map((item, index) => {
            const lineAmount = item.amount ?? item.quantity * item.unitPrice;
            return (
              <View key={`${item.inventoryItemId}-${index}`} style={styles.tableRow}>
                <Text style={[styles.colDescription, styles.tableCellText]}>{item.name}</Text>
                <Text style={[styles.colQty, styles.tableCellText]}>{item.quantity}</Text>
                <Text style={[styles.colRate, styles.tableCellText]}>
                  {formatCurrency(item.unitPrice, currencySymbol)}
                </Text>
                <Text style={[styles.colAmount, styles.tableCellText]}>
                  {formatCurrency(lineAmount, currencySymbol)}
                </Text>
              </View>
            );
          })}
        </View>

        <View style={styles.totalsWrapper}>
          <View style={styles.paymentInfo}>
            <Text style={styles.paymentText}>
              {invoiceNote ? `Notes\n${invoiceNote}\n\n${company.name}` : company.name}
            </Text>
          </View>
          <View style={styles.totalsBlock}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal</Text>
              <Text style={styles.totalValue}>{formatCurrency(itemSubtotal, currencySymbol)}</Text>
            </View>
            {discountAmount > 0 ? (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>
                  {discountType === 'percent' ? `Discount (${discountValue}%)` : 'Discount'}
                </Text>
                <Text style={styles.totalValue}>-{formatCurrency(discountAmount, currencySymbol)}</Text>
              </View>
            ) : null}
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Shipping</Text>
              <Text style={styles.totalValue}>{formatCurrency(shippingAmount, currencySymbol)}</Text>
            </View>
            {/* BALANCE DUE used to double as the invoice total. Now that a paid
                invoice shows 0 there, the total needs its own row or the
                customer never sees what the invoice was actually for. */}
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total</Text>
              <Text style={styles.totalValue}>{formatCurrency(invoiceTotal, currencySymbol)}</Text>
            </View>
            <View style={styles.balanceRow}>
              <Text style={styles.balanceLabel}>BALANCE DUE</Text>
              <Text style={styles.balanceValue}>
                {formatCurrency(balanceDue, currencySymbol)}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
